import React, { useMemo, useRef, useState } from "react";

import { useUpdateCompany } from "api/api-hooks/company-hooks";
import { useDeleteDashboard, useUpdateManagedDashboards } from "api/api-hooks/dashboards-hooks";
import { DashboardSimpleDto, Implementation } from "api/model/dashboard/Dashboard";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CheckIcon from "components/basic/check-icon/CheckIcon";
import DataTable from "components/basic/data-table/DataTable";
import Search from "components/basic/search/Search";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { useHasPermissions } from "hooks/useHasPermissions";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { DataTable as DataTableProps, DataTableRowReorderEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { SelectButton } from "primereact/selectbutton";
import { SelectItem } from "primereact/selectitem";
import { T } from "services/lang/LanguageService";
import { DEFAULT_VIEW } from "utils/constants";
import { DASHBOARD_ID_REGEX, DASHBOARD_JS_ID_REGEX } from "utils/regex";
import { fmtDatetime } from "utils/time-utils";
import { moveItem } from "utils/utils";
import { Link } from "react-router-dom";
import CreateDashboardForm from "./CreateDashboardForm";
import EditDashboardForm from "./EditDashboardForm";

type DashboardManagementTableProps = {
  dashboards: DashboardSimpleDto[];
  isLoading: boolean;
};

const DashboardManagementTable: React.FC<DashboardManagementTableProps> = ({ dashboards, isLoading }) => {
  const [dashboardType, setDashboardType] = useState<"ALL" | "DASHBOARD" | "REPORT">("DASHBOARD");
  const [search, setSearch] = useState("");
  const ref = useRef<DataTableProps<DashboardSimpleDto[]>>(null);
  const isAdmin = useHasPermissions("ADMIN");
  const tz = useDisplayTimezone();

  const { mutate: updateManagedDashboards, isPending } = useUpdateManagedDashboards();

  const filteredDashboards = useMemo(() => {
    if (dashboardType === "ALL") {
      return dashboards.filter(
        dashboard => !search || dashboard.title.toLowerCase().includes(search.toLowerCase())
      );
    }
    const types: Implementation[] =
      dashboardType === "DASHBOARD" ? ["JS", "STANDARD"] : ["REPORTS_JS", "REPORTS_JS_V2"];
    return dashboards.filter(
      dashboard =>
        types.includes(dashboard.implementation) &&
        (!search || dashboard.title.toLowerCase().includes(search.toLowerCase()))
    );
  }, [dashboardType, dashboards, search]);

  const onRowReorder = (e: DataTableRowReorderEvent<DashboardSimpleDto[]>) => {
    const { dragIndex, dropIndex } = e;
    if (dragIndex === dropIndex) return;

    const processedData = ref.current?.getProcessedData();
    if (!processedData) return;

    const sortedWeightDashboards = dashboards.sort((a, b) => a.sortWeight - b.sortWeight);

    const draggedItemId = processedData[e.dragIndex].id;
    const targetId = processedData[e.dropIndex].id;
    const draggedItemIndex = sortedWeightDashboards.findIndex(d => d.id === draggedItemId);
    const targetIndex = sortedWeightDashboards.findIndex(d => d.id === targetId);
    const reordered = moveItem(sortedWeightDashboards, draggedItemIndex, targetIndex);
    const mapped = reordered.map((dashboard, index) => ({
      ...dashboard,
      sortWeight: dashboard.assignedUsers.length || dashboard.companyShared ? index + 1 : index + 1001,
    }));
    updateManagedDashboards(mapped);
  };

  const renderTitle = (rowData: DashboardSimpleDto) => {
    let link;
    let isBlank = false;
    switch (rowData.implementation) {
      case "STANDARD":
        link = `${window.location.origin}/EASAS/views/dataservices/dataservicedashboard.xhtml?dashboardId=${rowData.id}&tabId=0`;
        isBlank = true;
        break;
      case "REPORTS_JS":
      case "REPORTS_JS_V2":
        link = `${window.location.origin}/EASAS/views/dataservices/dashboard-report.xhtml?dashboardId=${rowData.id}&tabId=0`;
        isBlank = true;
        break;
      default:
        link = `/dashboard/${rowData.id}/tab/0`;
    }
    return (
      <Link to={link} target={isBlank ? "_blank" : undefined}>
        {rowData.title}
      </Link>
    );
  };

  const renderPublic = (rowData: DashboardSimpleDto) => <CheckIcon enabled={rowData.companyShared} />;

  const renderActions = (rowData: DashboardSimpleDto) => <DashboardActions dashboard={rowData} />;

  const columns: TableColumn<DashboardSimpleDto>[] = [
    {
      columnKey: "draggable",
      header: "",
      rowReorder: true,
      className: "w-2rem",
    },
    {
      field: "id",
      header: "ID",
      columnKey: "id",
      sortable: true,
    },
    {
      field: "title",
      header: T("word.title"),
      columnKey: "title",
      body: renderTitle,
      sortable: true,
    },
    {
      field: "implementation",
      header: T("word.type"),
      columnKey: "implementation",
      sortable: true,
    },
    {
      field: "companyTitle",
      header: T("word.company"),
      columnKey: "companyTitle",
      hidden: !isAdmin,
    },
    {
      field: "assignedUsers",
      header: T("word.shared-for"),
      columnKey: "assignedUsers",
      body: rowData =>
        rowData.companyShared
          ? T("word.all")
          : `${rowData.assignedUsers.length} ${T("word.users").toLocaleLowerCase()}`,
    },
    {
      field: "hiddenForUsers",
      header: T("word.hidden-for"),
      columnKey: "hiddenForUsers",
      body: rowData =>
        rowData.hiddenForUsers?.length
          ? `${rowData.hiddenForUsers.length} ${T("word.users").toLocaleLowerCase()}`
          : "-",
    },
    {
      field: "companyShared",
      header: T("word.public"),
      columnKey: "companyShared",
      body: renderPublic,
    },
    {
      field: "modifiedAt",
      header: T("word.updated-at"),
      columnKey: "modifiedAt",
      body: rowData => rowData.modifiedAt && fmtDatetime(rowData.modifiedAt, tz),
      sortable: true,
    },
    {
      field: "modifiedByDisplay",
      header: T("word.updated-by"),
      columnKey: "modifiedByDisplay",
    },
    {
      field: "reportVersion",
      header: T("word.report-version"),
      columnKey: "reportVersion",
      hidden: dashboardType === "DASHBOARD",
    },
    {
      field: "sortWeight",
      header: T("word.sort-weight"),
      columnKey: "sortWeight",
      sortable: true,
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
      className: "w-6rem",
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={filteredDashboards}
      tableConfig={{
        ref: ref,
        loading: isLoading || isPending,
        scrollable: true,
        scrollHeight: "63vh",
        paginator: false,
        reorderableRows: true,
        onRowReorder,
        sortField: "sortWeight",
        sortOrder: 1,
        header: (
          <TableHeader
            dashboards={dashboards}
            dashboardType={dashboardType}
            setDashboardType={setDashboardType}
            search={search}
            setSearch={setSearch}
          />
        ),
      }}
    />
  );
};

export default DashboardManagementTable;

const DashboardActions: React.FC<{ dashboard: DashboardSimpleDto }> = ({ dashboard }) => {
  const [editDialog, setEditDialog] = useState(false);
  const { mutate: deleteDashboard, isPending } = useDeleteDashboard();

  const handleCloseDialog = () => {
    setEditDialog(false);
  };

  return (
    <>
      <ActionButtons
        onClickDelete={() => deleteDashboard(dashboard.id)}
        loadingDelete={isPending}
        onClickEdit={() => setEditDialog(true)}
      />
      <Dialog
        header={T("word.dashboard")}
        visible={editDialog}
        onHide={handleCloseDialog}
        style={{ minWidth: "30rem" }}
      >
        <EditDashboardForm dashboard={dashboard} onClose={handleCloseDialog} />
      </Dialog>
    </>
  );
};

type TableHeaderProps = {
  dashboards: DashboardSimpleDto[];
  dashboardType: "ALL" | "DASHBOARD" | "REPORT";
  setDashboardType: React.Dispatch<React.SetStateAction<"ALL" | "DASHBOARD" | "REPORT">>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
};

const TableHeader: React.FC<TableHeaderProps> = ({
  dashboards,
  dashboardType,
  setDashboardType,
  search,
  setSearch,
}) => {
  const company = useCurrentCompanyContext();
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const { mutate: updateCompany, isPending } = useUpdateCompany();

  const handleCloseDialog = () => {
    setOpenCreateDialog(false);
  };

  const options: SelectItem[] = [
    {
      label: T("word.dashboards"),
      value: "DASHBOARD",
    },
    {
      label: T("word.reports"),
      value: "REPORT",
    },
    {
      label: T("word.all"),
      value: "ALL",
    },
  ];

  const initDashboardOptions: SelectItem[] = useMemo(() => {
    const list = dashboards.reduce((items, item) => {
      if (!item.companyShared || item.implementation.startsWith("REPORTS")) return items;
      items.push({
        label: item.title,
        value: item.id,
      });
      return items;
    }, [] as SelectItem[]);
    return [{ label: "Index", value: -1 }, ...list];
  }, [dashboards]);

  const initDashboard = extractDashboardId(company?.initPage);

  const handleInitDashboardChange = (e: DropdownChangeEvent) => {
    if (!company) return;
    let newInitPage = DEFAULT_VIEW;
    if (e.value === -1) {
      updateCompany({ ...company, initPage: newInitPage });
      return;
    }

    const dashboard = dashboards.find(d => d.id === e.value);
    if (dashboard) {
      if (dashboard?.implementation === "STANDARD") {
        newInitPage = `/views/dataservices/dataservicedashboard.xhtml?dashboardId=${dashboard.id}&tabId=0`;
      } else {
        newInitPage = `/ui/#/dashboard/${dashboard.id}/tab/0`;
      }
    }
    updateCompany({ ...company, initPage: newInitPage });
  };

  return (
    <div className="flex flex-column md:flex-row gap-2 justify-content-between align-items-center">
      <div className="flex gap-3 align-items-center">
        <Button
          icon="pi pi-plus"
          label={T("word.add")}
          className="dashboard-editor-button icon-green border-green"
          onClick={() => setOpenCreateDialog(true)}
        />
        <Search value={search} onChange={setSearch} />
      </div>
      <div>
        <SelectButton value={dashboardType} onChange={e => setDashboardType(e.value)} options={options} />
      </div>
      <div className="flex gap-2 align-items-center">
        <span>{`${T("label.initial-page")}:`}</span>
        <Dropdown
          value={initDashboard}
          options={initDashboardOptions}
          onChange={handleInitDashboardChange}
          loading={isPending}
        />
      </div>
      <Dialog
        header={T("word.dashboard")}
        visible={openCreateDialog}
        onHide={handleCloseDialog}
        style={{ minWidth: "30rem" }}
      >
        <CreateDashboardForm onClose={handleCloseDialog} />
      </Dialog>
    </div>
  );
};

const extractDashboardId = (url: string | undefined) => {
  if (!url || url === DEFAULT_VIEW) return -1;
  const match = url.match(DASHBOARD_ID_REGEX);
  if (match) {
    return parseInt(match[1], 10);
  }
  const jsMatch = url.match(DASHBOARD_JS_ID_REGEX);
  if (jsMatch) {
    return parseInt(jsMatch[1], 10);
  }
  return -1;
};
